import React, { useId } from "react";
import { SKINS, HAIR_COLORS, OUTFIT_COLORS, hexDarken, normalizeAppearance } from "@/lib/appearance";

// Parametric illustrated Naija character drawn as SVG (no external assets).
// view: "full" (standing) | "bust" (head + shoulders) | "head"
// walking: subtle leg/arm sway for roam mode. flip: mirror horizontally.
export default function NaijaAvatar({
  appearance,
  view = "full",
  size = 120,
  walking = false,
  flip = false,
  className = "",
  style = {},
}) {
  const a = normalizeAppearance(appearance);
  const uid = useId().replace(/:/g, "");
  const skin = SKINS[a.skin];
  const hairCol = HAIR_COLORS[a.hairColor];
  const out = OUTFIT_COLORS[a.outfitColor];
  const outDark = hexDarken(out, 0.25);
  const trouser =
    a.outfit === "street" ? "#3557A0"
    : a.outfit === "jersey" ? "#1E293B"
    : a.outfit === "corporate" ? "#1F2937"
    : hexDarken(out, 0.35);
  const female = a.body === "f";

  const viewBox = view === "head" ? "24 16 72 92" : view === "bust" ? "6 16 108 118" : "0 0 120 212";
  const h = view === "full" ? size * 212 / 120 : view === "bust" ? size * 118 / 108 : size;

  return (
    <svg
      width={size}
      height={h}
      viewBox={viewBox}
      className={className}
      style={{ transform: flip ? "scaleX(-1)" : undefined, overflow: "visible", ...style }}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <pattern id={`ank${uid}`} width="10" height="10" patternUnits="userSpaceOnUse">
          <rect width="10" height="10" fill={out} />
          <path d="M5 1 L9 5 L5 9 L1 5 Z" fill={outDark} />
          <circle cx="5" cy="5" r="1.4" fill={out} />
        </pattern>
        <linearGradient id={`sk${uid}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={skin.fill} />
          <stop offset="1" stopColor={skin.dark} />
        </linearGradient>
      </defs>

      {/* ---------- LEGS (full only) ---------- */}
      {view === "full" && a.outfit !== "agbada" && (
        <g>
          <g style={{ transformOrigin: "52px 150px" }}>
            {walking && <animateTransform attributeName="transform" type="rotate" values="7;-7;7" dur="0.5s" repeatCount="indefinite" />}
            <rect x="48" y="150" width="11" height="46" rx="5" fill={trouser} />
            <rect x="46" y="192" width="15" height="9" rx="3" fill="#15110c" />
          </g>
          <g style={{ transformOrigin: "68px 150px" }}>
            {walking && <animateTransform attributeName="transform" type="rotate" values="-7;7;-7" dur="0.5s" repeatCount="indefinite" />}
            <rect x="61" y="150" width="11" height="46" rx="5" fill={trouser} />
            <rect x="59" y="192" width="15" height="9" rx="3" fill="#15110c" />
          </g>
        </g>
      )}

      {/* ---------- BODY / OUTFIT ---------- */}
      <OutfitBody outfit={a.outfit} out={out} outDark={outDark} skin={skin} female={female} patternId={`ank${uid}`} />

      {/* gold chain accessory */}
      {a.accessory === "chain" && (
        <path d="M50 108 Q60 122 70 108" fill="none" stroke="#FFD24A" strokeWidth="2.4" strokeLinecap="round" />
      )}

      {/* ---------- NECK + HEAD ---------- */}
      <rect x="54" y="90" width="12" height="14" rx="4" fill={skin.dark} />
      {/* ears */}
      <ellipse cx="37" cy="72" rx="5" ry="6.5" fill={skin.fill} />
      <ellipse cx="83" cy="72" rx="5" ry="6.5" fill={skin.fill} />
      {a.accessory === "earring" && <circle cx="37" cy="79" r="2.1" fill="#FFD24A" />}
      {/* head */}
      <ellipse cx="60" cy="68" rx="23" ry="26" fill={`url(#sk${uid})`} />

      {/* hair behind (afro/locs/braids volume) */}
      <HairBack hair={a.hair} hairCol={hairCol} />

      {/* face */}
      <g>
        {/* brows */}
        <path d="M45 61 q7 -4 13 -1" stroke={hexDarken(hairCol, 0.1)} strokeWidth="2.2" fill="none" strokeLinecap="round" />
        <path d="M62 60 q7 -3 13 1" stroke={hexDarken(hairCol, 0.1)} strokeWidth="2.2" fill="none" strokeLinecap="round" />
        {/* eyes */}
        <ellipse cx="51" cy="68" rx="3.6" ry="4.2" fill="#fff" />
        <ellipse cx="69" cy="68" rx="3.6" ry="4.2" fill="#fff" />
        <circle cx="51.6" cy="68.6" r="1.9" fill="#231a12" />
        <circle cx="69.6" cy="68.6" r="1.9" fill="#231a12" />
        {/* nose */}
        <path d="M60 71 q-2.5 5 -0.5 7.5 q2 1.2 3.5 0" fill="none" stroke={skin.dark} strokeWidth="1.6" strokeLinecap="round" />
        {/* smile */}
        <path d="M52 83 q8 6 16 0" fill="none" stroke={hexDarken(skin.dark, 0.3)} strokeWidth="2.2" strokeLinecap="round" />
        {female && <path d="M52 84 q8 4.5 16 0" fill="none" stroke="#C94F6D" strokeWidth="2" strokeLinecap="round" />}
      </g>

      {/* shades */}
      {a.accessory === "glasses" && (
        <g fill="#15110c">
          <rect x="44" y="63" width="14" height="9" rx="2.5" />
          <rect x="62" y="63" width="14" height="9" rx="2.5" />
          <rect x="57" y="66" width="6" height="2" />
        </g>
      )}

      {/* hair front / caps */}
      <HairFront hair={a.hair} hairCol={hairCol} out={out} outDark={outDark} />

      {/* cap accessory (on top of hair) */}
      {a.accessory === "cap" && (
        <g>
          <path d="M38 55 q22 -22 44 0 q-22 -9 -44 0 Z" fill={out} />
          <path d="M34 55 q6 4 10 3 l-2 -5 q-5 -1 -8 2 Z" fill={outDark} />
        </g>
      )}
    </svg>
  );
}

function OutfitBody({ outfit, out, outDark, skin, female, patternId }) {
  // shared sleeves/hands helper
  const hands = (
    <>
      <circle cx="36" cy="138" r="6" fill={skin.fill} />
      <circle cx="84" cy="138" r="6" fill={skin.fill} />
    </>
  );
  if (outfit === "agbada" || outfit === "owambe") {
    const robe = outfit === "owambe" ? out : out;
    return (
      <g>
        {/* flowing robe */}
        <path d="M60 96 L40 118 L30 200 L90 200 L80 118 Z" fill={robe} />
        <path d="M60 96 L40 118 L34 165 L60 160 L86 165 L80 118 Z" fill={outDark} opacity="0.5" />
        {/* wide sleeves */}
        <path d="M44 112 L20 150 L34 156 L50 128 Z" fill={robe} />
        <path d="M76 112 L100 150 L86 156 L70 128 Z" fill={robe} />
        <circle cx="27" cy="152" r="5.5" fill={skin.fill} />
        <circle cx="93" cy="152" r="5.5" fill={skin.fill} />
        {/* neckline / fila hint */}
        <path d="M52 100 L60 112 L68 100 Z" fill={outDark} />
        <rect x="52" y="120" width="16" height="42" rx="3" fill={outDark} opacity="0.35" />
      </g>
    );
  }
  // common torso base
  const torso = (fill) => (
    <>
      {/* sleeves */}
      <rect x="32" y="104" width="12" height="34" rx="6" fill={fill} />
      <rect x="76" y="104" width="12" height="34" rx="6" fill={fill} />
      {hands}
      {/* body */}
      <path d={`M44 104 Q60 98 76 104 L${female ? 82 : 80} 158 Q60 166 ${female ? 38 : 40} 158 Z`} fill={fill} />
    </>
  );
  if (outfit === "corporate") {
    return (
      <g>
        {torso("#263244")}
        {/* shirt + tie */}
        <path d="M52 104 L60 150 L68 104 Z" fill="#F4F6FA" />
        <path d="M58 106 L62 106 L63 134 L60 140 L57 134 Z" fill={out} />
        <path d="M44 104 L52 104 L58 114 L52 118 Z" fill="#1b2430" />
        <path d="M76 104 L68 104 L62 114 L68 118 Z" fill="#1b2430" />
      </g>
    );
  }
  if (outfit === "jersey") {
    return (
      <g>
        {torso(out)}
        <rect x="56" y="104" width="8" height="56" fill="#fff" opacity="0.85" />
        <text x="60" y="140" fontSize="16" fontWeight="800" fill={outDark} textAnchor="middle" fontFamily="Arial">10</text>
      </g>
    );
  }
  if (outfit === "ankara") {
    return (
      <g>
        {torso(`url(#${patternId})`)}
        <path d="M44 104 Q60 98 76 104 L76 110 Q60 104 44 110 Z" fill={outDark} />
      </g>
    );
  }
  // street
  return (
    <g>
      {torso(out)}
      <path d="M52 100 Q60 108 68 100 L66 106 Q60 110 54 106 Z" fill={outDark} />
    </g>
  );
}

function HairBack({ hair, hairCol }) {
  if (hair === "afro") return <circle cx="60" cy="54" r="30" fill={hairCol} />;
  if (hair === "locs")
    return (
      <g fill={hairCol}>
        <ellipse cx="60" cy="50" rx="27" ry="22" />
        {[32, 40, 80, 88].map((x, i) => <rect key={i} x={x - 2.5} y="60" width="5" height="48" rx="2.5" />)}
      </g>
    );
  if (hair === "braids")
    return (
      <g fill={hairCol}>
        <ellipse cx="60" cy="50" rx="27" ry="21" />
        {[34, 41, 79, 86].map((x, i) => (
          <g key={i}>
            <rect x={x - 3} y="60" width="6" height="50" rx="3" />
            <circle cx={x} cy="110" r="3" fill="#FFD24A" />
          </g>
        ))}
      </g>
    );
  return null;
}

function HairFront({ hair, hairCol, out, outDark }) {
  if (hair === "bald") return null;
  if (hair === "afro")
    return <path d="M36 60 Q60 30 84 60 Q84 44 60 40 Q36 44 36 60 Z" fill={hairCol} />;
  if (hair === "fade")
    return <path d="M38 62 Q40 40 60 38 Q80 40 82 62 Q74 50 60 50 Q46 50 38 62 Z" fill={hairCol} />;
  if (hair === "cornrows")
    return (
      <g>
        <path d="M38 60 Q40 40 60 38 Q80 40 82 60 Q74 48 60 48 Q46 48 38 60 Z" fill={hairCol} />
        {[46, 52, 60, 68, 74].map((x, i) => (
          <path key={i} d={`M${x} 46 Q${x} 54 ${x} 60`} stroke={hexDarken(hairCol, 0.4)} strokeWidth="1.6" fill="none" />
        ))}
      </g>
    );
  if (hair === "locs" || hair === "braids")
    return <path d="M38 58 Q42 40 60 38 Q78 40 82 58 Q74 48 60 48 Q46 48 38 58 Z" fill={hairCol} />;
  if (hair === "durag")
    return (
      <g fill={hexDarken(hairCol, 0.1)}>
        <path d="M37 60 Q40 38 60 37 Q80 38 83 60 Q60 50 37 60 Z" />
        <path d="M78 48 L96 40 L94 52 L80 56 Z" />
        <path d="M80 54 L98 54 L92 64 L80 60 Z" />
      </g>
    );
  if (hair === "gele")
    return (
      <g>
        <path d="M34 56 Q36 28 60 28 Q84 28 86 56 Q60 44 34 56 Z" fill={out} />
        <path d="M34 54 Q60 40 86 54 Q78 20 60 24 Q42 20 34 54 Z" fill={outDark} />
        <path d="M60 24 Q92 10 96 40 Q80 30 60 34 Z" fill={out} />
      </g>
    );
  return null;
}
